import { debounce } from "lodash";
import { useEffect, useState } from "react";

import { GetDoc, GetWSMgr } from "@/Utils/StateLib";
import { appstr } from "@coreapp/utils/appstr";
import { QHighlightMgr } from "@coreapp/CoreApp/QHighlightMgr";
import { QHighlight } from "@coreapp/CoreApp/QHighlight";
import { kDefaultNewHighlightColor } from "@coreapp/constants";
import { QRule } from "@coreapp/CoreApp/QRule";
import { enumMatchSubs } from "@coreapp/constants";
import { useChartRuleController } from "../Utils/useChartRuleController";

type Props = {
  manager: QHighlightMgr;
};

const HIGHLIGHTS_LOCAL_STORAGE_KEY = "highlightgroup";

export const useHighlightController = (props?: Props) => {
  const [interactiveHighlights, setInteractiveHighlights] = useState<QHighlight[]>(() => {
    return props?.manager.GetInteractiveHighlights() || [];
  });
  const [sortedFilteredHighlights, setSortedFilteredHighlights] =
    useState<QHighlight[]>(interactiveHighlights);
  const [sortAsc, setSortAsc] = useState<boolean>(true);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [highlightEditorState, setHighlightEditorState] = useState<QHighlight | null>(null);

  const indexHighlightMap = new Map<number | null, QHighlight>();
  sortedFilteredHighlights.forEach(highlight => {
    const index = highlight.GetUID();
    if (!index) return;
    indexHighlightMap.set(index, highlight);
  });

  const { getUniqueBlockName, handleNameChangeValidation } = useChartRuleController({
    entity: "Highlight",
    elements: interactiveHighlights
  });

  useEffect(() => {
    setInteractiveHighlights(props?.manager.GetInteractiveHighlights() || []);
  }, [props?.manager.fInteractiveHighlight]);

  useEffect(() => {
    let nextHighlights = [...interactiveHighlights];

    /*
    For sorting and searching. Disabled for now.
    if (searchTerm) {
      nextHighlights = nextHighlights.filter((highlight) => {
        return highlight.GetName()?.toLowerCase().includes(searchTerm.toLowerCase());
      });
    }

    nextHighlights.sort((a, b) => {
      const nameA = a.GetName()?.toLowerCase() ?? "";
      const nameB = b.GetName()?.toLowerCase() ?? "";

      if (nameA < nameB) {
        return sortAsc ? -1 : 1;
      }
      if (nameA > nameB) {
        return sortAsc ? 1 : -1;
      }
      return 0;
    });
    */

    setSortedFilteredHighlights(nextHighlights);
  }, [interactiveHighlights, searchTerm, sortAsc]);

  // Editing actions
  const editBlock =
    (action: (highlight: QHighlight, ...args: any[]) => void) =>
    (highlight: QHighlight, ...args: any[]) => {
      const nextHighlights = [...interactiveHighlights];
      const existingHighlight = indexHighlightMap.get(highlight.GetUID());
      if (!existingHighlight) {
        return;
      }

      action(highlight, ...args);

      handleHighlightsChange(nextHighlights);
    };

  const handleHighlightNameChange = editBlock((highlight: QHighlight, newName: string) => {
    highlight.SetName(newName);
  });

  const handleHighlightToggle = editBlock((highlight: QHighlight) => {
    highlight.SetHighlightApplied(!highlight.GetHighlightApplied());
  });

  const handleHighlightsChange = (nextHighlights: QHighlight[]) => {
    props?.manager.SetInteractiveHighlights(nextHighlights);
    handleHighlightsPersistence(nextHighlights);
    setInteractiveHighlights(nextHighlights);
  };

  const handleHighlightsPersistence = (nextHighlights: QHighlight[]) => {
    let fullList: QHighlight[] = props?.manager.GetFullGroupList() as QHighlight[];

    fullList = fullList.filter(
      (x: QHighlight) => x.GetGroupIdentifier() !== props?.manager.GetFilterGroupIdentifier()
    );
    fullList = fullList.concat(nextHighlights);

    if (fullList.length > 0) {
      const stringElements = fullList.map(h => JSON.stringify(h.OutputToJSON()));
      localStorage[HIGHLIGHTS_LOCAL_STORAGE_KEY] = JSON.stringify(stringElements);
    } else {
      delete localStorage[HIGHLIGHTS_LOCAL_STORAGE_KEY];
    }

    props?.manager.PersistUserHighlights(fullList);

    const wsMgr = GetWSMgr();
    wsMgr?.ApplyChartHighlights();
    wsMgr?.SignalDocChanged();
  };

  const handleCommit = (highlight: QHighlight) => {
    const nextHighlights = [...interactiveHighlights];
    const existingHighlight = indexHighlightMap.get(highlight.GetUID());
    if (existingHighlight) {
      const index = nextHighlights.findIndex(h => h.GetUID() === highlight.GetUID());
      nextHighlights[index] = highlight;
    } else {
      nextHighlights.push(highlight);
    }

    setHighlightEditorState(null);
    handleHighlightsChange(nextHighlights);
  };

  const handleSearch = debounce((value: string) => {
    setSearchTerm(value);
  }, 300);

  const toggleSort = () => {
    setSortAsc(!sortAsc);
  };

  const handleRemoveHighlight = (highlight: QHighlight) => {
    const nextHighlights = [...interactiveHighlights];
    const index = nextHighlights.findIndex(h => h.GetUID() === highlight.GetUID());
    if (index !== -1) {
      nextHighlights.splice(index, 1);
      handleHighlightsChange(nextHighlights);
    }
  };

  const getNumberOfActiveHighlights = () => {
    return interactiveHighlights.filter(h => h.GetHighlightApplied()).length;
  };

  const handleStartEditing = (highlight: QHighlight) => {
    setHighlightEditorState(highlight);
  };

  const handleStopEditing = () => {
    setHighlightEditorState(null);
  };

  const generateUID = () => {
    const currentUIDs = interactiveHighlights.map(h => h.GetUID()).filter(uid => uid != null);

    return Math.max(0, ...currentUIDs) + 1;
  };

  const handleNewHighlight = () => {
    const newHighlight = new QHighlight();
    const newRule = new QRule();
    newRule.InitRule(enumMatchSubs.matchonly, 0, false);
    newRule.AddBlankRulePair();
    newRule.EnsureRulesUIDs();

    newHighlight.InitHighlight("", newRule, true, kDefaultNewHighlightColor, true);

    const chartID = GetDoc()?.GetChartID();
    if (chartID) newHighlight.SetChartID(chartID);
    newHighlight.SetGroupIdentifier(props?.manager.GetFilterGroupIdentifier() || "");
    newHighlight.SetUID(generateUID());

    handleStartEditing(newHighlight);
  };

  const getCopyName = (originalName: string): string => {
    const existingNames = new Set(interactiveHighlights.map(h => h.GetName() ?? ""));
    const suffix = appstr("copy");
    let candidate = `${originalName} ${suffix}`;
    if (!existingNames.has(candidate)) return candidate;
    let i = 2;
    while (existingNames.has(`${originalName} ${suffix} ${i}`)) i++;
    return `${originalName} ${suffix} ${i}`;
  };

  const buildCopiedHighlight = (definition: any): QHighlight => {
    const newHighlight = new QHighlight();
    newHighlight.LoadFromJSON(definition);
    newHighlight.SetHighlightApplied(false);
    newHighlight.SetName(getCopyName(definition.name || ""));

    const chartID = GetDoc()?.GetChartID();
    if (chartID) newHighlight.SetChartID(chartID);
    newHighlight.SetGroupIdentifier(props?.manager.GetFilterGroupIdentifier() || "");
    newHighlight.SetUID(generateUID());

    return newHighlight;
  };

  const handleCopyHighlight = (definition: any) => {
    handleHighlightsChange([...interactiveHighlights, buildCopiedHighlight(definition)]);
  };

  const handleCopyAndEditHighlight = (definition: any) => {
    const newHighlight = buildCopiedHighlight(definition);
    handleHighlightsChange([...interactiveHighlights, newHighlight]);
    handleStartEditing(newHighlight);
  };

  return {
    getUniqueBlockName,
    handleNewHighlight,
    handleCommit,
    handleCopyHighlight,
    handleCopyAndEditHighlight,
    handleHighlightNameChange,
    handleHighlightToggle,
    handleNameChangeValidation,
    handleRemoveHighlight,
    handleStartEditing,
    handleStopEditing,
    handleSearch,
    toggleSort,
    sortedFilteredHighlights,
    highlightEditorState,
    getNumberOfActiveHighlights
  };
};
